import { service } from '@ember/service';

import type { DSSetup, RecordData, Service } from 'webapp-types';
import {
	BillingFeature,
	DateRangeTypeId,
	ModelName,
	TeamSectionItemType,
} from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';

import DateRange from 'webapp/models-custom/date-range';
import MetricLimitations from 'webapp/models-custom/metric-limitations';
import TeamSectionItem from 'webapp/models-custom/team-section-item';
import type TeamSectionItemRecord from 'webapp/models/team-section-item';

import ModelManagerForStoreService from 'webapp/services/model-manager/for-store';

export default class ModelManagerTeamSectionItemsService extends ModelManagerForStoreService<
	TeamSectionItemRecord,
	TeamSectionItem
> {
	@service('account') declare accountService: Service.Account;
	@service('billing') declare billingService: Service.Billing;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('current-user') declare currentUserService: Service.CurrentUser;

	getRecordsForSection(sectionId: number): TeamSectionItemRecord[] {
		return this.savedRecords.filter((item) => item.sectionId === sectionId);
	}

	preparePayloadForUpdate(
		sectionItem: TeamSectionItem,
	): Promise<RecordData.TeamSectionItem.ObjectData> {
		const metricSetup = sectionItem.metricSetup,
			dateRangeModel = DateRange.createFromFormula(sectionItem.dateRange);

		return this.prepareCommonPayload(dateRangeModel, metricSetup.metricSetupData!);
	}

	preparePayloadForCreate(
		metricSetupData: DSSetup.MetricSetupData,
	): Promise<RecordData.TeamSectionItem.ObjectData> {
		const dateRange = DateRange.predefinedRanges
			.find((item) => item.rangeTypeId === DateRangeTypeId.MONTH_TO_DATE)!
			.createCopy();
		dateRange.selectOptimalGranulation();
		return this.prepareCommonPayload(dateRange, metricSetupData);
	}

	delete(sectionItem: TeamSectionItem) {
		if (this.accountService.handleDemo()) {
			return;
		}

		const affirmativeFunc = () => {
			const infolayer = this.infolayerService.loading('Removing section item...');

			this.analyticsService.event(TrackingEvent.TEAM_PAGE_EDITED, {
				action: ['item removed'],
				objectType: this.getItemTypeName(sectionItem.objectType),
			});

			sectionItem
				.delete()
				.then(
					() => {
						infolayer.ok('Section item removed!');
					},
					(error) => {
						infolayer.close();
						throw error;
					},
				)
				.catch(this.requestService.handleGenericThrownError.bind(this.requestService));
		};

		this.popupService.openConfirmPopup({
			onConfirm: affirmativeFunc,
			title: `Remove "${sectionItem.title}"?`,
			text: '',
			confirmAction: `Remove item`,
			cancelAction: 'Cancel',
		});
	}

	openLimitationPopup(item: TeamSectionItem) {
		if (item.isReport) {
			this.billingService.checkIfCan(BillingFeature.REPORTS_BASIC, true);
		} else if (item.isGoal) {
			this.billingService.checkIfCan(BillingFeature.GOALS, true);
		}
	}

	getItemTypeName(itemType: TeamSectionItemType) {
		if (itemType === TeamSectionItemType.METRIC) {
			return 'metric';
		} else if (itemType === TeamSectionItemType.GOAL) {
			return 'goal';
		} else if (itemType === TeamSectionItemType.DATABOARD) {
			return 'databoard';
		} else if (itemType === TeamSectionItemType.REPORT) {
			return 'report';
		} else if (itemType === TeamSectionItemType.DATABOARD_LOOP) {
			return 'loop';
		}

		return 'unknown';
	}

	private prepareCommonPayload(
		dateRangeModel: DateRange,
		metricSetupData: DSSetup.MetricSetupData,
	): Promise<RecordData.TeamSectionItem.ObjectData> {
		return new Promise((resolve) => {
			const dateRangeCopy = dateRangeModel.createCopy(),
				limitations = new MetricLimitations({
					metrics: [
						{
							key: metricSetupData.key,
							spaceAccessId: metricSetupData.spaceAccessId,
						},
					],
					dateRange: dateRangeCopy,
				});

			void limitations.limitationsLoaded?.then(() => {
				limitations.selectOptimalValidGranulation();

				const payload = {
					dateRange: limitations.dateRange?.formula,
					metricSetup: metricSetupData,
				};

				resolve(payload);
			});
		});
	}

	// ------ For-store ------

	modelName = ModelName.TEAM_SECTION_ITEM;

	createModel(model: TeamSectionItemRecord): TeamSectionItem {
		return new TeamSectionItem({ model });
	}
}

declare module '@ember/service' {
	interface Registry {
		'model-manager/team-section-items': ModelManagerTeamSectionItemsService;
	}
}
